import * as THREE from 'three';
import type { BattleRules, BattleState } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { GlowLayer } from './glow.ts';
import { WORLD_PER_HEX } from './coords.ts';

/**
 * The old trap (La Quemadura 3, docs/QUEMADURA.md): the first Vigil's harpoon engine, half sunk in the Burn's glass at
 * the clearing's edge and aimed across it. A carriage of black beams on its side, a harpoon three men long in its
 * groove, the bow arms that throw it; two big winches either side that turn as heroes wind them, ropes from them to
 * the engine; a lever out beside the line on its own rope. On the ground, the line the harpoon flies down: faint while
 * it hangs slack, alive once wound.
 *
 * The shot: the harpoon drawn back while wound, a jerk of the lever's rope at the pull, then it flies down the line in
 * a blink and sticks in the glass at the far end (glass bursting off what it ran through); it shows back in the groove
 * once the winding starts again.
 *
 * Draw calls: the engine (one merged mesh), the harpoon, two winch drums, the lever arm, the line, a glow layer. No lights.
 */

type Trap = NonNullable<BattleRules['trap']>;

const BEAM = ['#1f1a1a', '#262021', '#1a1617'];
const IRON = '#4a4644';
const IRON_DARK = '#2e2b2b';
const ROPE = '#6b5a4a';
const PALE = new THREE.Color('#dfe9ff');
const EMBER = new THREE.Color('#ff4f3a');
const GLASS = '#cfe2ff';

/** How long the harpoon takes down the line (s), and how far it is drawn back once wound (world). */
const FLIGHT = 0.16;
const DRAWN = 0.38;

const line = (len: number): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    uniforms: { uOn: { value: 0 }, uT: { value: 0 }, uLen: { value: len } },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      uniform float uOn;
      uniform float uT;
      uniform float uLen;
      varying vec2 vUv;
      void main() {
        // Across: two bold edges and a faint floor between them; along: chevrons running away from the engine.
        float x = abs(vUv.x - 0.5) * 2.0;
        float edge = smoothstep(0.62, 0.8, x) * (1.0 - smoothstep(0.93, 1.0, x));
        float along = vUv.y * uLen;
        float chev = fract(along * 0.55 - abs(vUv.x - 0.5) * 0.9 - uT * mix(0.12, 0.8, uOn));
        float mark = smoothstep(0.0, 0.06, chev) * (1.0 - smoothstep(0.22, 0.32, chev)) * (1.0 - smoothstep(0.6, 0.75, x));
        // Fade in at the near end and out at the far one.
        float ends = smoothstep(0.0, 0.05, vUv.y) * (1.0 - smoothstep(0.9, 1.0, vUv.y));
        vec3 slack = vec3(0.78, 0.6, 0.55);
        vec3 live = vec3(1.0, 0.31, 0.23);
        vec3 col = mix(slack, live, uOn);
        float a = (edge * mix(0.24, 0.95, uOn) + mark * mix(0.09, 0.6, uOn) + mix(0.03, 0.12, uOn)) * ends;
        a *= 0.88 + 0.12 * sin(uT * 6.0) * uOn;
        gl_FragColor = vec4(col, a);
      }`,
  });

export class TrapLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(64);
  private harpoon: THREE.Mesh;
  private drums: THREE.Mesh[] = [];
  private arm: THREE.Mesh;
  private lineMat: THREE.ShaderMaterial;
  /** The engine's mouth and where the line ends (world), and its yaw. */
  private mouth: THREE.Vector3;
  private far: THREE.Vector3;
  private dir: THREE.Vector3;
  private yaw: number;
  private rest = new THREE.Matrix4();
  /** The harpoon in flight (since, s) or stuck at the far end; how drawn back it is (eased). */
  private flying = -1;
  private stuck = false;
  private drawn = 0;
  private armed = false;
  private wound: number[] = [];
  private turn: number[] = [];
  private pulled = -1;
  private now = 0;
  private winchAt: THREE.Vector3[];
  private leverAt: THREE.Vector3;

  constructor(
    private ctx: OmenCtx,
    private r: Trap,
  ) {
    this.group.name = 'trap';
    const k = WORLD_PER_HEX / 1000;
    const at = ctx.at(r.at.x, r.at.y, 0);
    this.dir = new THREE.Vector3(r.ax / 10000, 0, r.ay / 10000).normalize();
    this.yaw = Math.atan2(this.dir.x, this.dir.z);
    const side = new THREE.Vector3(this.dir.z, 0, -this.dir.x);
    this.winchAt = r.winches.map((w) => ctx.at(w.x, w.y, 0));
    this.leverAt = ctx.at(r.lever.x, r.lever.y, 0);
    this.wound = r.winches.map(() => 0);
    this.turn = r.winches.map(() => 0);

    // The engine: a carriage of black beams, tilted back where its rear sank, the bow arms over its front.
    const body = new Kit();
    const g = ctx.ground(at.x, at.z);
    const m = place(at.x, g - 0.12, at.z, this.yaw, 1, -0.12, 0.08);
    const beam = (i: number): string => BEAM[i % BEAM.length] as string;
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 7);
    // The prod: one great arc of iron across the front, its middle forward, swept back to its tips.
    const ARC = Math.PI * 0.62;
    const prod = new THREE.TorusGeometry(1.0, 0.07, 5, 18, ARC).rotateZ(-Math.PI / 2 - ARC / 2).rotateX(-Math.PI / 2);
    // Two side rails, cross beams, the groove the harpoon lies in.
    for (const s of [-1, 1]) {
      body.add(box, beam(s + 2), m, place(s * 0.34, 0.5, 0, 0, new THREE.Vector3(0.14, 0.18, 2.6)));
      // The legs: a pair at the front, the back pair gone under the glass.
      body.add(box, beam(s + 3), m, place(s * 0.36, 0.2, 0.95, 0, new THREE.Vector3(0.12, 0.62, 0.12), 0.1, s * 0.12));
      body.add(box, beam(s + 1), m, place(s * 0.36, 0.08, -0.9, 0, new THREE.Vector3(0.12, 0.4, 0.12), -0.2, s * 0.15));
    }
    for (const z of [-1.0, -0.3, 0.4, 1.05]) body.add(box, beam(Math.round(z * 3)), m, place(0, 0.48, z, 0, new THREE.Vector3(0.82, 0.1, 0.16)));
    body.add(box, '#141011', m, place(0, 0.61, 0.05, 0, new THREE.Vector3(0.2, 0.06, 2.5)));
    // The bow: the prod over the front (its arc's middle a little ahead of the head), tips curled back, its string
    // run back to the slider behind the harpoon.
    body.add(prod, IRON, m, place(0, 0.74, 0.3));
    const tipX = Math.sin(ARC / 2);
    const tipZ = 0.3 + Math.cos(ARC / 2);
    for (const s of [-1, 1]) {
      body.add(new THREE.ConeGeometry(0.08, 0.3, 5), IRON_DARK, m, place(s * tipX, 0.74, tipZ - 0.05, 0, 1, -Math.PI / 2 + 0.5, s * 0.5));
      // The string: from each tip back to the slider.
      const a = new THREE.Vector3(s * tipX, 0.74, tipZ - 0.15);
      const b = new THREE.Vector3(0, 0.74, -0.55);
      const mid = a.clone().add(b).multiplyScalar(0.5);
      const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), b.clone().sub(a).normalize());
      body.add(cyl, '#8a7a68', m, new THREE.Matrix4().compose(mid, q, new THREE.Vector3(0.025, a.distanceTo(b), 0.025)));
    }
    // The slider the string pulls, and the iron bands that hold the prod to the rails.
    body.add(box, IRON, m, place(0, 0.7, -0.55, 0, new THREE.Vector3(0.3, 0.12, 0.16)));
    for (const s of [-1, 1]) body.add(box, IRON_DARK, m, place(s * 0.34, 0.66, 1.18, 0, new THREE.Vector3(0.18, 0.2, 0.14)));
    // A head of iron plates over the front, riveted.
    body.add(box, IRON_DARK, m, place(0, 0.7, 1.28, 0, new THREE.Vector3(0.6, 0.3, 0.12)));
    for (const x of [-0.22, 0, 0.22]) body.add(new THREE.SphereGeometry(0.035, 5, 4), IRON, m, place(x, 0.78, 1.35));
    // The windlass at the back: a drum the two ropes come into, a pawl.
    body.add(cyl, beam(0), m, place(0, 0.62, -1.15, 0, new THREE.Vector3(0.24, 0.8, 0.24), 0, Math.PI / 2));
    body.add(box, IRON, m, place(0.3, 0.72, -1.05, 0, new THREE.Vector3(0.05, 0.2, 0.05), 0.4));
    // Glass crept up its foot: dark glossy lumps against it.
    const lump = new THREE.IcosahedronGeometry(0.5, 0);
    for (const [x, z, s] of [[-0.5, -0.9, 0.5], [0.45, -0.7, 0.42], [0.2, -1.25, 0.6], [-0.4, 0.2, 0.3]] as const) body.add(lump, '#0e0b0d', m, place(x, 0.0, z, x * 3, new THREE.Vector3(s, s * 0.45, s)));
    // The winches: a frame either side, and the ropes from them to the engine's windlass.
    const back = new THREE.Vector3(0, 0.62, -1.15).applyMatrix4(m);
    const rope = (a: THREE.Vector3, b: THREE.Vector3, sag: number): void => {
      const n = 8;
      for (let i = 0; i < n; i++) {
        const p0 = a.clone().lerp(b, i / n);
        const p1 = a.clone().lerp(b, (i + 1) / n);
        p0.y -= Math.sin((i / n) * Math.PI) * sag;
        p1.y -= Math.sin(((i + 1) / n) * Math.PI) * sag;
        const len = p0.distanceTo(p1);
        const mid = p0.clone().add(p1).multiplyScalar(0.5);
        const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), p1.clone().sub(p0).normalize());
        body.add(cyl, ROPE, new THREE.Matrix4().compose(mid, q, new THREE.Vector3(0.035, len, 0.035)));
      }
    };
    for (const w of this.winchAt) {
      const wg = ctx.ground(w.x, w.z);
      const wy = Math.atan2(w.x - at.x, w.z - at.z);
      const fm = place(w.x, wg, w.z, wy);
      for (const s of [-1, 1]) {
        body.add(box, beam(s + 4), fm, place(s * 0.32, 0.4, 0, 0, new THREE.Vector3(0.12, 0.85, 0.16)));
        body.add(box, beam(s + 5), fm, place(s * 0.32, 0.06, 0, 0, new THREE.Vector3(0.18, 0.12, 0.6)));
      }
      body.add(new THREE.CylinderGeometry(0.45, 0.45, 0.06, 10), '#0e0b0d', place(w.x, wg - 0.02, w.z));
      rope(new THREE.Vector3(w.x, wg + 0.62, w.z), back, 0.25);
    }
    // The lever: a post, its rope run back to the engine along the ground.
    const lg = ctx.ground(this.leverAt.x, this.leverAt.z);
    const ly = Math.atan2(at.x - this.leverAt.x, at.z - this.leverAt.z);
    const lm = place(this.leverAt.x, lg, this.leverAt.z, ly);
    body.add(box, beam(1), lm, place(0, 0.35, 0, 0, new THREE.Vector3(0.16, 0.7, 0.16)));
    body.add(box, beam(2), lm, place(0, 0.05, 0, 0, new THREE.Vector3(0.5, 0.1, 0.5)));
    for (let i = 0; i < 3; i++) body.add(new THREE.DodecahedronGeometry(0.12, 0), '#4a4446', lm, place(Math.cos(i * 2.1) * 0.3, 0.06, Math.sin(i * 2.1) * 0.3));
    rope(new THREE.Vector3(this.leverAt.x, lg + 0.5, this.leverAt.z), new THREE.Vector3(at.x, g + 0.45, at.z), 0.32);
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, metalness: 0.12, flatShading: true });
    this.mats.push(mat);
    const engine = body.build(mat, 'trap-engine');
    if (engine) {
      engine.castShadow = !ctx.low;
      engine.receiveShadow = true;
      this.geos.push(engine.geometry);
      this.group.add(engine);
    }

    // The harpoon: a long black shaft, a barbed iron head, three fins of hide at its tail.
    const hk = new Kit();
    const id = new THREE.Matrix4();
    hk.add(cyl, '#6a5848', id, place(0, 0, 0, 0, new THREE.Vector3(0.1, 2.4, 0.1), Math.PI / 2));
    for (const z of [-0.6, 0.2]) hk.add(new THREE.CylinderGeometry(0.065, 0.065, 0.07, 7), IRON, id, place(0, 0, z, 0, 1, Math.PI / 2));
    hk.add(new THREE.ConeGeometry(0.17, 0.5, 4), '#a8a4a0', id, place(0, 0, 1.42, 0, 1, Math.PI / 2));
    for (const s of [-1, 1]) hk.add(new THREE.ConeGeometry(0.06, 0.3, 3), '#8a8682', id, place(s * 0.12, 0, 1.15, 0, 1, -Math.PI / 2 + 0.3, s * 0.5));
    hk.add(new THREE.CylinderGeometry(0.1, 0.1, 0.14, 6), IRON, id, place(0, 0, 1.12, 0, 1, Math.PI / 2));
    for (let i = 0; i < 3; i++) hk.add(new THREE.BoxGeometry(0.012, 0.26, 0.36), '#8a2e26', id, place(Math.sin((i / 3) * Math.PI * 2) * 0.1, Math.cos((i / 3) * Math.PI * 2) * 0.1, -1.05, 0, 1, 0, -(i / 3) * Math.PI * 2));
    const hmat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.6, metalness: 0.3, flatShading: true, emissive: '#000000' });
    this.mats.push(hmat);
    this.harpoon = hk.build(hmat, 'trap-harpoon') as THREE.Mesh;
    this.harpoon.matrixAutoUpdate = false;
    this.harpoon.castShadow = !ctx.low;
    this.geos.push(this.harpoon.geometry);
    this.group.add(this.harpoon);
    this.rest.copy(m).multiply(place(0, 0.72, 0.15));

    // The winch drums: turned as they are wound (one mesh each: a drum, four spokes, the rope round it).
    const dk = new Kit();
    dk.add(cyl, beam(0), id, place(0, 0, 0, 0, new THREE.Vector3(0.38, 0.5, 0.38), 0, Math.PI / 2));
    dk.add(cyl, ROPE, id, place(0, 0, 0, 0, new THREE.Vector3(0.42, 0.3, 0.42), 0, Math.PI / 2));
    // A spoked wheel at its end to wind it by: rim, hub and four spokes, with handles out past the rim.
    dk.add(new THREE.TorusGeometry(0.46, 0.04, 4, 14).rotateY(Math.PI / 2), '#3a2e26', id, place(0.3, 0, 0));
    for (let i = 0; i < 4; i++) {
      dk.add(box, '#3a2e26', id, place(0.3, 0, 0, 0, new THREE.Vector3(0.05, 0.92, 0.06), (i / 4) * Math.PI));
      const a = (i / 4) * Math.PI * 2;
      dk.add(new THREE.CylinderGeometry(0.03, 0.03, 0.22, 5), '#6a5848', id, place(0.4, Math.cos(a) * 0.52, Math.sin(a) * 0.52, 0, 1, 0, Math.PI / 2));
    }
    const dmat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, flatShading: true });
    this.mats.push(dmat);
    const drum = dk.build(dmat, 'trap-drum') as THREE.Mesh;
    this.geos.push(drum.geometry);
    for (let i = 0; i < this.winchAt.length; i++) {
      const d = i === 0 ? drum : drum.clone();
      d.matrixAutoUpdate = false;
      this.drums.push(d);
      this.group.add(d);
    }

    // The lever's arm: a beam pivoted on the post.
    const ak = new Kit();
    ak.add(box, '#3a2e26', id, place(0, 0.3, 0, 0, new THREE.Vector3(0.08, 0.7, 0.08)));
    ak.add(new THREE.SphereGeometry(0.08, 6, 4), IRON, id, place(0, 0.66, 0));
    const amat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, flatShading: true });
    this.mats.push(amat);
    this.arm = ak.build(amat, 'trap-lever') as THREE.Mesh;
    this.arm.matrixAutoUpdate = false;
    this.geos.push(this.arm.geometry);
    this.group.add(this.arm);

    // The line: a ribbon on the ground from `near` to `len`, ±`half`, laid over the glass's ups and downs.
    const near = r.near * k;
    const len = r.len * k;
    const half = r.half * k;
    const n = 40;
    const pos: number[] = [];
    const uv: number[] = [];
    const idx: number[] = [];
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      for (const sx of [-1, 1]) {
        const p = at.clone().addScaledVector(this.dir, near + (len - near) * t).addScaledVector(side, sx * half);
        pos.push(p.x, ctx.ground(p.x, p.z) + 0.04, p.z);
        uv.push(sx < 0 ? 0 : 1, t);
      }
      if (i < n) idx.push(i * 2, i * 2 + 1, i * 2 + 2, i * 2 + 1, i * 2 + 3, i * 2 + 2);
    }
    const lg2 = new THREE.BufferGeometry();
    lg2.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    lg2.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    lg2.setIndex(idx);
    this.lineMat = line(len - near);
    this.mats.push(this.lineMat);
    this.geos.push(lg2);
    const lm2 = new THREE.Mesh(lg2, this.lineMat);
    lm2.renderOrder = 2;
    lm2.frustumCulled = false;
    this.group.add(lm2);

    this.mouth = at.clone().addScaledVector(this.dir, 1.3).setY(g + 0.62);
    this.far = at.clone().addScaledVector(this.dir, len);
    this.far.y = ctx.ground(this.far.x, this.far.z);
    this.group.add(this.glow.mesh);
    this.place();
  }

  /** Every fresh sim tick: the winding, the pull and the shot. */
  tick(s: BattleState): void {
    const st = s.trap;
    const low = this.ctx.low;
    if (st) {
      this.armed = st.armed;
      st.wound.forEach((w, i) => {
        if (w > (this.wound[i] ?? 0)) this.turn[i] = (this.turn[i] ?? 0) + 1;
        this.wound[i] = w;
      });
      // Winding again: the harpoon is back in its groove.
      if (this.stuck && st.wound.some((w) => w > 0)) {
        this.stuck = false;
        this.ctx.vfx.burst(this.mouth, { n: low ? 3 : 6, color: '#6a6466', speed: 0.01, up: 0.015, size: 0.25, life: 18, smoke: true, spread: 0.3 });
      }
    }
    for (const e of s._events ?? []) {
      if (e.t !== 'trap') continue;
      const p = this.ctx.at(e.x, e.y, 0.3);
      if (e.kind === 'wound') {
        this.ctx.vfx.burst(p, { n: low ? 4 : 8, color: '#a89894', speed: 0.02, up: 0.03, size: 0.2, life: 16, smoke: true, spread: 0.3 });
        this.ctx.vfx.ring(this.ctx.at(e.x, e.y, 0.05), 0.2, 1.1, '#dfe9ff', 12, 0.6);
      } else if (e.kind === 'armed') {
        this.ctx.flare(this.mouth, '#ffd0c0', 2.4, 14);
        this.ctx.vfx.burst(this.mouth, { n: low ? 5 : 10, color: '#ff8a6e', speed: 0.02, up: 0.03, size: 0.05, life: 18, gravity: -0.001, spread: 0.4 });
      } else if (e.kind === 'pull') {
        this.pulled = this.now;
        this.ctx.vfx.burst(this.ctx.at(e.x, e.y, 0.1), { n: low ? 3 : 6, color: '#8a8478', speed: 0.025, up: 0.02, size: 0.25, life: 16, smoke: true, spread: 0.4 });
      } else if (e.kind === 'shot') {
        this.flying = this.now;
        this.stuck = false;
        this.ctx.shake(0.14);
        this.ctx.flare(this.mouth, '#ffe2c8', 3.2, 10);
        this.ctx.vfx.burst(this.mouth, { n: low ? 5 : 12, color: '#7a7270', speed: 0.04, up: 0.02, size: 0.3, life: 20, smoke: true, spread: 0.4 });
        // A wake of dust down the line.
        const steps = low ? 5 : 10;
        for (let i = 1; i <= steps; i++) {
          const q = this.mouth.clone().lerp(this.far, i / steps);
          q.y = this.ctx.ground(q.x, q.z) + 0.15;
          this.ctx.vfx.burst(q, { n: 1, color: '#8a8282', speed: 0.01, up: 0.01, size: 0.35, life: 14 + i, smoke: true, spread: 0.15 });
        }
      } else if (e.kind === 'hit' || e.kind === 'skewer') {
        const big = e.kind === 'hit';
        this.ctx.flare(p, big ? '#e8f0ff' : '#ffd6c0', big ? 3.6 : 2, big ? 14 : 8);
        this.ctx.vfx.burst(p.setY(p.y + (big ? 0.6 : 0.2)), { n: low ? (big ? 12 : 5) : big ? 28 : 10, color: GLASS, speed: big ? 0.08 : 0.05, up: 0.05, size: 0.07, life: 22, gravity: 0.008, spread: 0.5 });
        this.ctx.vfx.burst(p, { n: low ? 3 : 6, color: '#ff8a6e', speed: 0.05, up: 0.04, size: 0.05, life: 16, gravity: 0.004, spread: 0.3 });
        if (big) {
          this.ctx.vfx.ring(this.ctx.at(e.x, e.y, 0.05), 0.3, 2.4, '#dfe9ff', 18, 0.7);
          this.ctx.shake(0.3);
        }
      } else if (e.kind === 'miss') {
        // It sticks in the glass at the line's end with nothing on it.
        this.ctx.vfx.burst(this.far.clone().setY(this.far.y + 0.2), { n: low ? 4 : 10, color: '#0e0b0d', speed: 0.04, up: 0.04, size: 0.08, life: 18, gravity: 0.008, spread: 0.3 });
      }
    }
  }

  /** Every redraw: the harpoon (in its groove, drawn back, flying or stuck), the drums, the lever, the line, the glints. */
  draw(): void {
    this.place();
    const u = this.lineMat.uniforms as { uT: THREE.IUniform<number>; uOn: THREE.IUniform<number> };
    u.uT.value = this.now;
    u.uOn.value += ((this.armed ? 1 : 0) - u.uOn.value) * 0.12;
    this.glow.begin();
    const tip = new THREE.Vector3(0, 0, 1.4).applyMatrix4(this.harpoon.matrix);
    if (this.armed) {
      this.glow.add(tip.x, tip.y, tip.z, EMBER, 1.1, 0.3 + 0.08 * Math.sin(this.now * 5));
      this.glow.add(this.leverAt.x, this.ctx.ground(this.leverAt.x, this.leverAt.z) + 0.8, this.leverAt.z, EMBER, 0.9, 0.35 + 0.15 * Math.sin(this.now * 4));
    }
    this.winchAt.forEach((w, i) => {
      const full = (this.wound[i] ?? 0) >= this.r.wind;
      if (!this.armed && this.flying < 0) this.glow.add(w.x, this.ctx.ground(w.x, w.z) + 0.9, w.z, full ? EMBER : PALE, 0.8, full ? 0.25 : 0.14 + 0.06 * Math.sin(this.now * 3 + i));
    });
    this.glow.end();
  }

  private place(): void {
    // The harpoon: drawn back while wound; in flight, down the line; then stuck at the far end.
    const target = this.armed ? 1 : 0;
    this.drawn += (target - this.drawn) * 0.15;
    if (this.flying >= 0) {
      const t = Math.min(1, (this.now - this.flying) / FLIGHT);
      const p = this.mouth.clone().lerp(this.far, t);
      p.y = this.mouth.y + (this.far.y + 0.3 - this.mouth.y) * t;
      this.harpoon.matrix.compose(p, new THREE.Quaternion().setFromEuler(new THREE.Euler(0.05, this.yaw, 0, 'YXZ')), new THREE.Vector3(1, 1, 1));
      if (t >= 1) {
        this.flying = -1;
        this.stuck = true;
      }
    } else if (this.stuck) {
      const p = this.far.clone().addScaledVector(this.dir, -0.6).setY(this.far.y + 0.4);
      this.harpoon.matrix.compose(p, new THREE.Quaternion().setFromEuler(new THREE.Euler(0.35, this.yaw, 0.05, 'YXZ')), new THREE.Vector3(1, 1, 1));
    } else {
      this.harpoon.matrix.copy(this.rest).multiply(new THREE.Matrix4().makeTranslation(0, 0, -DRAWN * this.drawn));
    }
    this.harpoon.matrixWorldNeedsUpdate = true;
    // The drums turn while wound, a little each tick of winding.
    this.drums.forEach((d, i) => {
      const w = this.winchAt[i] as THREE.Vector3;
      const yaw = Math.atan2(w.x - this.mouth.x, w.z - this.mouth.z);
      const a = (this.turn[i] ?? 0) * 0.35;
      d.matrix.compose(new THREE.Vector3(w.x, this.ctx.ground(w.x, w.z) + 0.62, w.z), new THREE.Quaternion().setFromEuler(new THREE.Euler(a, yaw, 0, 'YXZ')), new THREE.Vector3(1, 1, 1));
      d.matrixWorldNeedsUpdate = true;
    });
    // The lever: up, and thrown down for a moment at the pull.
    const since = this.pulled >= 0 ? this.now - this.pulled : 9;
    const tilt = since < 0.5 ? -1.0 * Math.sin(Math.min(1, since / 0.12) * Math.PI * 0.5) : since < 1.2 ? -1.0 * (1 - (since - 0.5) / 0.7) : 0;
    const lg = this.ctx.ground(this.leverAt.x, this.leverAt.z);
    const ly = Math.atan2(this.mouth.x - this.leverAt.x, this.mouth.z - this.leverAt.z);
    this.arm.matrix.compose(new THREE.Vector3(this.leverAt.x, lg + 0.62, this.leverAt.z), new THREE.Quaternion().setFromEuler(new THREE.Euler(0.35 + tilt, ly, 0, 'YXZ')), new THREE.Vector3(1, 1, 1));
    this.arm.matrixWorldNeedsUpdate = true;
  }

  frame(now: number): void {
    this.now = now;
  }

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
  }
}
